import { isAxiosError } from "axios";
import { env } from "src/utils/env";
import { postRequest } from "src/utils/axios";
import { PogAgentRunRequest, PogAgentRunResponse } from "./types";

/**
 * Base URL of the POG agent service. Empty when the deployment has no agent, in
 * which case the chatbox stays hidden.
 */
export const pogAgentUrl = () => (env.VITE_POG_AGENT_URL || "").replace(/\/+$/, "");

export const isPogAgentEnabled = () => pogAgentUrl() !== "";

/**
 * One agent turn. The OWA login token goes out as the Bearer token — the agent
 * only ever uses it to read and write POGs back through OWA, never to talk to
 * the LLM.
 */
export const postPogAgentRun = async (
  payload: PogAgentRunRequest,
): Promise<PogAgentRunResponse> => {
  const response = await postRequest<PogAgentRunResponse>(`${pogAgentUrl()}/v1/run`, { payload });

  return response.data;
};

const errorDetail = (error: unknown): unknown =>
  isAxiosError(error)
    ? (error.response?.data as { detail?: unknown } | undefined)?.detail
    : undefined;

/**
 * The agent could not tell what kind of request the prompt was. It classifies
 * by keyword first and asks its planner second; when neither settles it, it
 * answers 400 with "could not classify intent … pass intent explicitly". That
 * is a question for the merchandiser, not a failure, so the panel offers the
 * intents inline instead of showing the error.
 */
export const isIntentClarificationError = (error: unknown): boolean => {
  const detail = errorDetail(error);

  return (
    isAxiosError(error) &&
    error.response?.status === 400 &&
    typeof detail === "string" &&
    detail.includes("could not classify intent")
  );
};

/**
 * What went wrong, in a shape the panel can put into words in the current
 * locale. `agent` is a reply the agent chose to send (FastAPI's `detail`);
 * `unreachable` never got one; `unknown` is anything thrown on our side.
 */
export type PogAgentErrorInfo =
  | { kind: "agent"; status: number | "?"; detail: string }
  | { kind: "unreachable"; detail: string }
  | { kind: "unknown"; detail: string };

/** Anything that can cut a run short: an error, or a batch whose pages stopped advancing. */
export type PogAgentFailure = PogAgentErrorInfo | { kind: "batchStalled" };

export const describePogAgentError = (error: unknown): PogAgentErrorInfo => {
  if (isAxiosError(error)) {
    const detail = errorDetail(error);
    const status = error.response?.status ?? "?";

    if (typeof detail === "string" && detail) {
      return error.response ? { kind: "agent", status, detail } : { kind: "unknown", detail };
    }

    if (detail !== undefined) {
      return { kind: "agent", status, detail: JSON.stringify(detail) };
    }

    if (error.response) {
      return { kind: "agent", status, detail: error.response.statusText || "request failed" };
    }

    return { kind: "unreachable", detail: error.message };
  }

  return { kind: "unknown", detail: error instanceof Error ? error.message : String(error) };
};
